import { AlertTriangle, CreditCard, FileText, Info, Landmark, Receipt, Wallet } from "lucide-react";
import type { ReactNode } from "react";

import { COLORS, TrendChart } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { ClickableCard } from "@/components/ClickableCard";
import { Drillable } from "@/components/Drillable";
import { EmptyState } from "@/components/EmptyState";
import { FinArOverTimeChart } from "@/components/FinArOverTimeChart";
import { FinDoubleFlagSection } from "@/components/FinDoubleFlagSection";
import { FinLatePaymentContext } from "@/components/FinLatePaymentContext";
import { InsightCallout } from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import { MeterBar } from "@/components/MeterBar";
import { MetricLabel } from "@/components/MetricTooltip";
import { NotesList } from "@/components/NotesList";
import { PageHeader } from "@/components/PageHeader";
import { useScope } from "@/context/ScopeContext";
import { useCrossFlags, useFinanceMoM } from "@/hooks/queries";
import { formatInt, formatUsd, formatUsdCompact } from "@/lib/format";
import { seriesDelta } from "@/lib/trend";

// Finance — the CFO sidecar. Every dollar here is BusRight's OWN AR posture and warehouse
// procurement cost, never customer revenue. AR balance, credit-limit utilization, invoice
// balances, and TTM procurement spend, PLUS the outstanding-vs-paid MoM area graph (the
// credit-hold story) and late-payment context. Color law: no RAG on financial figures — the
// credit gauge is a neutral brand meter, and the AR area graph uses the sequential ramp, not RAG.

export const FinancePage = (): ReactNode => {
  const { data, isLoading, isError, error } = useFinanceMoM();
  const cross = useCrossFlags();
  const { scope } = useScope();
  const meta = data?.meta;
  const payload = data?.data;
  const kpis = payload?.kpis;
  const mom = payload?.mom;

  const spend = payload?.monthlySpend ?? [];
  const spendDelta = seriesDelta(spend);
  const usedCredit = kpis ? kpis.creditLimitUsd - kpis.remainingCreditLimitUsd : 0;
  const utilization = kpis && kpis.creditLimitUsd > 0 ? usedCredit / kpis.creditLimitUsd : 0;
  const ttmSpend = spend.reduce((sum, p) => sum + p.value, 0);

  // AR × hardware-risk double-flags: districts that carry open AR AND trip the warehouse trouble
  // flag. The honest headline counts these; figures come straight off the cross-flags payload.
  const crossPayload = cross.data?.data;
  const doubleFlags = crossPayload?.doubleFlags ?? [];
  const totalOpenAr = crossPayload?.totalOpenArUsd ?? null;
  const doubleFlagAr = doubleFlags.reduce((sum, r) => sum + r.openArUsd, 0);

  if (isError) {
    return (
      <>
        <PageHeader title="Finance" meta={meta} />
        <ChartCard title="Finance">
          <EmptyState
            title="Couldn't load finance"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Finance"
        description="BusRight's own AR / warehouse cost — NOT customer revenue. Credit posture, invoice balances, and procurement spend."
        meta={meta}
      />

      {crossPayload ? (
        <InsightCallout
          icon={Info}
          headline={
            doubleFlags.length > 0
              ? `${totalOpenAr !== null ? formatUsdCompact(totalOpenAr) : "—"} open AR; ${formatInt(doubleFlags.length)} district(s) carry BOTH open AR and a hardware-trouble flag.`
              : `${totalOpenAr !== null ? formatUsdCompact(totalOpenAr) : "—"} open AR; no district currently carries both AR and a hardware-trouble flag.`
          }
          stats={[
            ...(totalOpenAr !== null
              ? [{ label: "Total open AR", value: formatUsdCompact(totalOpenAr) }]
              : []),
            { label: "Double-flagged districts", value: formatInt(doubleFlags.length) },
            { label: "AR on double-flags", value: formatUsdCompact(doubleFlagAr) },
          ]}
          cta={{ label: "Open Districts", to: "/districts" }}
        />
      ) : null}

      <div className="grid kpi-band">
        <Drillable
          metricKey="finance.arBalanceUsd"
          scope={scope}
          label="Drill into AR current balance by month"
        >
          <KpiCard
            label={<MetricLabel metricKey="finance.arBalanceUsd" label="AR current balance" />}
            icon={CreditCard}
            loading={isLoading}
            value={kpis ? formatUsd(kpis.arCurrentBalanceUsd) : "—"}
            source="stg_e2_credits.ar_current_balance"
          />
        </Drillable>
        <Drillable
          metricKey="finance.creditRemainingUsd"
          scope={scope}
          label="Drill into remaining credit"
        >
          <KpiCard
            label={<MetricLabel metricKey="finance.creditRemainingUsd" label="Remaining credit" />}
            icon={Wallet}
            loading={isLoading}
            value={kpis ? formatUsd(kpis.remainingCreditLimitUsd) : "—"}
            source="stg_e2_credits.remaining_credit_limit"
          />
        </Drillable>
        <ClickableCard to="/districts" label="Open Districts — open-order AR by district">
          <KpiCard
            label="Open orders (AR)"
            icon={Landmark}
            loading={isLoading}
            value={kpis ? formatUsd(kpis.arTotalOpenOrdersUsd) : "—"}
            source="stg_e2_credits.ar_total_open_orders"
          />
        </ClickableCard>
        <Drillable
          metricKey="finance.arOutstandingUsd"
          scope={scope}
          label="Drill into outstanding balance by month"
        >
          <KpiCard
            label={<MetricLabel metricKey="finance.arOutstandingUsd" label="Outstanding balance" />}
            icon={Receipt}
            loading={isLoading}
            value={kpis ? formatUsd(kpis.outstandingBalanceUsd) : "—"}
            source="stg_e2_invoices.outstanding_balance"
          />
        </Drillable>
      </div>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title="Outstanding vs paid — the credit-hold story"
          subtitle="Month-over-month AR: outstanding vs paid. Watch the gap grow or close."
          source="stg_e2_invoices ⋈ stg_e2_credits (finance.mom)"
          loading={isLoading}
        >
          {mom && mom.series.length > 0 ? (
            <>
              <Drillable
                metricKey="finance.arOutstandingUsd"
                scope={scope}
                label="Drill into the monthly outstanding-vs-paid rows"
              >
                <FinArOverTimeChart series={mom.series} />
              </Drillable>
              <div style={{ marginTop: 12 }}>
                <FinLatePaymentContext
                  totalLatePaymentUsd={mom.totalLatePaymentUsd}
                  remainingCreditLimitUsd={mom.remainingCreditLimitUsd}
                  onCreditHold={mom.onCreditHold}
                  scope={scope}
                />
              </div>
            </>
          ) : (
            <EmptyState
              title="No MoM series"
              message="The finance.mom block may not be live yet."
            />
          )}
        </ChartCard>
      </div>

      <FinDoubleFlagSection
        doubleFlags={doubleFlags}
        totalOpenArUsd={totalOpenAr}
        doubleFlagArUsd={doubleFlagAr}
        rma={crossPayload?.rma ?? null}
        loading={cross.isLoading}
        isError={cross.isError}
        errorMessage={cross.error instanceof Error ? cross.error.message : "Unknown error"}
      />

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <ChartCard
          title="Credit posture"
          subtitle="Utilization vs $500K credit line (neutral meter — not a RAG state)"
          source="stg_e2_credits"
          loading={isLoading}
        >
          {kpis ? (
            <div>
              <MeterBar
                label="Credit used"
                fraction={utilization}
                valueText={`${(utilization * 100).toFixed(1)}%`}
              />
              <MeterBar
                label="Credit remaining"
                fraction={1 - utilization}
                valueText={formatUsdCompact(kpis.remainingCreditLimitUsd)}
              />
              <div className="metric-strip" style={{ marginTop: 12 }}>
                <div className="metric-chip">
                  <span className="mc-label">Credit limit</span>
                  <span className="mc-value br-tabular-nums">
                    {formatUsdCompact(kpis.creditLimitUsd)}
                  </span>
                </div>
                <div className="metric-chip">
                  <span className="mc-label">Credit used</span>
                  <span className="mc-value br-tabular-nums">{formatUsdCompact(usedCredit)}</span>
                </div>
              </div>
            </div>
          ) : (
            <EmptyState title="No credit data" />
          )}
        </ChartCard>

        <ChartCard
          title="Procurement spend"
          subtitle="Monthly warehouse cost since 2024 — NOT customer revenue"
          source="stg_e2_invoices.invoiced_amount"
          loading={isLoading}
          className="span-2"
          action={
            spendDelta !== null ? (
              <span className="card-source br-tabular-nums">
                TTM ≈ {formatUsdCompact(ttmSpend)}
              </span>
            ) : undefined
          }
        >
          {spend.length > 0 ? (
            <TrendChart data={spend} height={220} />
          ) : (
            <EmptyState title="No spend series" />
          )}
        </ChartCard>
      </div>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title="Invoice balances"
          subtitle="Lifetime invoicing and what's still outstanding"
          source="stg_e2_invoices"
          loading={isLoading}
        >
          {kpis ? (
            <div className="metric-strip">
              <Drillable metricKey="finance.invoiceCount" scope={scope} label="Drill into invoices">
                <div className="metric-chip">
                  <span className="mc-label">
                    <FileText size={13} aria-hidden />
                    <MetricLabel metricKey="finance.invoiceCount" label="Invoices" />
                  </span>
                  <span className="mc-value br-tabular-nums">{formatInt(kpis.invoiceCount)}</span>
                </div>
              </Drillable>
              <div className="metric-chip">
                <span className="mc-label">Invoiced (lifetime)</span>
                <span className="mc-value br-tabular-nums">
                  {formatUsdCompact(kpis.invoicedAmountUsd)}
                </span>
              </div>
              <Drillable
                metricKey="finance.arOutstandingUsd"
                scope={scope}
                label="Drill into outstanding balance"
              >
                <div className="metric-chip">
                  <span className="mc-label">
                    <MetricLabel metricKey="finance.arOutstandingUsd" label="Outstanding balance" />
                  </span>
                  <span className="mc-value br-tabular-nums">
                    {formatUsdCompact(kpis.outstandingBalanceUsd)}
                  </span>
                </div>
              </Drillable>
              <Drillable
                metricKey="finance.arBalanceUsd"
                scope={scope}
                label="Drill into AR current balance"
              >
                <div className="metric-chip">
                  <span className="mc-label">
                    <MetricLabel metricKey="finance.arBalanceUsd" label="AR current balance" />
                  </span>
                  <span className="mc-value br-tabular-nums">
                    {formatUsdCompact(kpis.arCurrentBalanceUsd)}
                  </span>
                </div>
              </Drillable>
            </div>
          ) : (
            <EmptyState title="No invoice data" />
          )}
        </ChartCard>
      </div>

      <div
        style={{
          marginTop: 12,
          padding: "10px 14px",
          fontSize: 12,
          color: COLORS.textSecondary,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
          background: COLORS.surface2,
        }}
      >
        Every dollar on this surface is BusRight's own AR / warehouse procurement cost — never
        customer revenue.
      </div>

      {meta ? (
        <div style={{ marginTop: 16 }}>
          <ChartCard title="Data notes" loading={isLoading}>
            <NotesList notes={meta.notes} />
          </ChartCard>
        </div>
      ) : null}
    </>
  );
};
